import { captureException } from '@sentry/react';
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  message,
  Modal,
  type ModalProps,
  Radio,
  type RadioChangeEvent,
} from 'antd';
import dayjs from 'dayjs';
import unset from 'lodash/unset';
import { useEffect, useState } from 'react';

import { useAdminBillingPeriodController_updateOneById } from '@api-client/generated/AdminBillingPeriodController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { RangePicker } from '@entities';
import {
  PRICE_FIELD_CLASS,
  PRICE_VAT_ROW_TESTID,
  PriceVatRow,
} from '@entities/billing/modals/styled';
import {
  grossPrice,
  roundMoney,
  VAT_RATE,
  VAT_RATE_CHOICES,
} from '@entities/billing/vat';
import { useCurrentCompanyId } from '@hooks';

import * as S from './styled';

type BillingPeriod = Schemas.BillingPeriod;
type InvoiceItemDto = Schemas.InvoiceItemDto;
type InvoiceItem = Schemas.InvoiceItem;

type FormFieldsDto = {
  period: [string, string] | string;
};

/**
 * Cent-snap a stored amount, leaving an absent one absent.
 *
 * The price and the total are snapped independently, on purpose: the gross total
 * is the authoritative amount — it is what the client's card is charged (the
 * backend charges `round(Σ totalPrice)`) and what the invoice PDF prints — so it
 * must not move beyond its own cent. Re-deriving it from the rounded price would
 * do exactly that: a line stored as 94.055 @ 17% (gross 110.04435, charged
 * 110.04) would become 94.06 × 1.17 = 110.05 and no longer match the charge.
 * The price is the breakdown and is the value that bends instead.
 */
const toCentsOrUndefined = (amount: number | undefined) =>
  amount == null ? undefined : roundMoney(amount);

type ModalBillingActionProps = {
  details?: BillingPeriod | null;
  selected: InvoiceItem | null;
  // Index of the edited item within details.items. Items carry no reliable id
  // (optional and non-unique), so edits are targeted by position.
  selectedIndex?: number | null;
  type: 'add' | 'edit';
  onCancel: () => void;
  onAfterUpdate: (updatedPeriod: BillingPeriod) => void;
} & ModalProps;

const ModalBillingAction = ({
  selected,
  selectedIndex,
  details,
  type,
  open,
  onCancel,
  onAfterUpdate,
  ...rest
}: ModalBillingActionProps) => {
  const companyId = useCurrentCompanyId();
  const [form] = Form.useForm();

  const [periodType, setPeriodType] = useState('period');

  const { mutate: updateBilling } =
    useAdminBillingPeriodController_updateOneById();

  useEffect(
    () => {
      if (selected && type == 'edit') {
        // Legacy items predate the rate picker and may carry no taxRate; show
        // the standard rate rather than leaving the radio group unselected.
        // Their amounts also predate cent rounding (a 94.05 line at 17% VAT was
        // stored as 110.0385), so snap them to cents on the way into the form —
        // otherwise re-saving the item carries the extra digits into the PDF.
        const values = {
          ...selected,
          taxRate: selected.taxRate ?? VAT_RATE,
          unitPrice: toCentsOrUndefined(selected.unitPrice),
          totalPrice: toCentsOrUndefined(selected.totalPrice),
        };

        if (selected.endDate) {
          form.setFieldsValue({
            ...values,
            period: [dayjs(selected.startDate), dayjs(selected.endDate)],
          });

          setPeriodType('period');
        } else {
          form.setFieldsValue({
            ...values,
            period: dayjs(selected.startDate),
          });

          setPeriodType('one-time');
        }
      }

      if (type == 'add') {
        form.resetFields();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, type]
  );

  // Recompute the VAT-inclusive total from the current price and rate whenever
  // either of them changes, so the stored total always matches the rate we
  // later submit.
  const recalcTotalPrice = (patch: {
    unitPrice?: number;
    taxRate?: number;
  }) => {
    const unitPrice = Number(patch.unitPrice ?? form.getFieldValue('unitPrice'));
    const taxRate = patch.taxRate ?? form.getFieldValue('taxRate') ?? VAT_RATE;

    form.setFieldsValue({
      totalPrice: Number.isFinite(unitPrice)
        ? grossPrice(unitPrice, taxRate)
        : undefined,
    });
  };

  const updateUnitPrice = (e: React.ChangeEvent<HTMLInputElement>) =>
    recalcTotalPrice({ unitPrice: +e.target.value });

  const updateTaxRate = (e: RadioChangeEvent) =>
    recalcTotalPrice({ taxRate: e.target.value as number });

  const handleSubmit = (values: InvoiceItemDto & FormFieldsDto) => {
    // Issued periods are read-only: don't mutate items if a billingDocument
    // appeared (e.g. via refetch) while this add/edit modal was open.
    if (details?.billingDocument) {
      onCancel();
      return;
    }

    // Persist whole cents: the amounts end up verbatim in the client's invoice
    // PDF, so a hand-typed 94.0512 must not survive as an extra decimal there.
    const body = {
      ...values,
      unitPrice: roundMoney(+values.unitPrice),
      taxRate: +(values?.taxRate || 0),
      totalPrice: roundMoney(+(values?.totalPrice || 0)),
    };

    if (periodType === 'period') {
      body.startDate = values.period[0];
      body.endDate = values.period[1];
    }

    if (periodType === 'one-time' && values.period) {
      body.startDate = values.period[0];
      unset(body, 'endDate');
    }

    unset(body, 'period');

    if (details) {
      const items =
        type === 'edit'
          ? details.items.map((item, index) => {
              if (index !== selectedIndex) {
                return item;
              }
              // Preserve fields the form doesn't manage (quantity, unit,
              // netPrice, settings); the form values override the rest.
              const updated = { ...item, ...body };
              // One-time services carry no end date.
              if (periodType === 'one-time') {
                unset(updated, 'endDate');
              }
              return updated;
            })
          : [body, ...details.items];

      updateBilling(
        {
          parameter: {
            companyId: companyId!,
            id: details.id,
          },
          requestBody: {
            ...details,
            items,
          },
        },
        {
          onSuccess: (response) => {
            onAfterUpdate(response);
            onCancel();

            setPeriodType('period');

            form.resetFields();
          },
          onError: (error) => {
            // Keep the modal open with the entered values so the user can retry.
            message.error('Failed to save the service');
            captureException(error);
          },
        }
      );
    }
  };

  return (
    <Modal
      // Wide enough for "Price before VAT" and the five VAT-rate buttons to
      // share a row without cramping either.
      width={640}
      open={open}
      title={type === 'add' ? 'Add new service' : 'Edit service'}
      footer={null}
      closeIcon={null}
      onCancel={onCancel}
      destroyOnClose
      centered
      {...rest}
    >
      <S.Inner gap={24} vertical>
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          requiredMark={false}
          initialValues={{
            isPrivate: false,
            taxRate: VAT_RATE,
          }}
        >
          <S.Wrapper>
            <Form.Item label="Name" name="name" rules={[{ required: true }]}>
              <Input size="large" />
            </Form.Item>

            <Form.Item label="Description (optional)" name="description">
              <Input.TextArea rows={4} size="large" />
            </Form.Item>
          </S.Wrapper>

          <S.Wrapper>
            <PriceVatRow gap={16} wrap data-testid={PRICE_VAT_ROW_TESTID}>
              <Form.Item
                className={PRICE_FIELD_CLASS}
                label="Price before VAT"
                name="unitPrice"
                rules={[{ required: true }]}
              >
                <Input size="large" onChange={updateUnitPrice} />
              </Form.Item>

              <Form.Item
                label="VAT rate"
                name="taxRate"
                rules={[{ required: true }]}
              >
                <Radio.Group
                  options={VAT_RATE_CHOICES}
                  optionType="button"
                  onChange={updateTaxRate}
                />
              </Form.Item>
            </PriceVatRow>

            <Form.Item
              label="Price"
              name="totalPrice"
              rules={[{ required: true }]}
            >
              <Input size="large" disabled />
            </Form.Item>
          </S.Wrapper>

          <S.Wrapper>
            <S.Types gap={8} align="center">
              <S.Type
                isSelected={periodType === 'period'}
                onClick={() => setPeriodType('period')}
              >
                Period
              </S.Type>

              <S.Type
                isSelected={periodType === 'one-time'}
                onClick={() => setPeriodType('one-time')}
              >
                One-time
              </S.Type>
            </S.Types>

            <Form.Item
              label="Billing period"
              name="period"
              rules={[{ required: true }]}
            >
              {periodType === 'period' && (
                <RangePicker
                  variant="outlined"
                  size="large"
                  style={{ width: '100%' }}
                />
              )}

              {periodType === 'one-time' && (
                <DatePicker
                  placeholder="DD.MM.YYYY"
                  format="DD.MM.YYYY"
                  size="large"
                  style={{ width: '50%' }}
                />
              )}
            </Form.Item>
          </S.Wrapper>

          <S.Submit>
            <Form.Item noStyle>
              <Flex justify="flex-end" gap={12}>
                <Button onClick={onCancel} size="large">
                  Cancel
                </Button>

                <Button type="primary" htmlType="submit" size="large">
                  {type === 'add' ? 'Add service' : 'Save'}
                </Button>
              </Flex>
            </Form.Item>
          </S.Submit>
        </Form>
      </S.Inner>
    </Modal>
  );
};

export default ModalBillingAction;
